import {
  Box,
  Divider,
  Flex,
  Image,
  SkeletonText,
  useToast,
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import CustomText from "../../components/text/CustomText";
import { IconAlertTriangleFilled, IconCheck } from "@tabler/icons-react";
import KYCVector from "../../svgs/KYCVector";
import CustomButton from "../../components/buttons/CustomButton";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getLoginData, updateUser } from "../../redux/actions/auth.actions";
import IconChevDown from "../../svgs/IconChevDown";
import UploadIcon from "../../svgs/UploadIcon";
import { useCreatePaymentIntent, useSendEmail } from "../../utils/wds.api";
import PaymentForm from "../auth/components/PaymentForm";
import { getAllPromos } from "../../redux/actions/general.actions";
import FormInput from "../../components/form/FormInput";

function KYCPending() {
  const [searchParams] = useSearchParams();
  const paymentIntent = searchParams.get("payment_intent");

  const toast = useToast();

  const { user, uid } = useSelector((state: any) => state.authUser);
  const { promos } = useSelector((state: any) => state.general);
  const dispatch: any = useDispatch();
  const navigate = useNavigate();

  const paymentInfo = useCreatePaymentIntent();
  const sendEmail = useSendEmail();

  const [paymentIntentInfo, setPaymentInfo] = useState<any>();
  const [step, setStep] = useState(1);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [loadingFor, setLoadingFor] = useState("");
  const [promo, setPromo] = useState<any>("");
  const [payload, setPayload] = useState<any>({
    documentType: "Passport",
    documentUrls: [],
    profileImg: "",
  });

  const img1Ref = useRef<any>(null);
  const img4Ref = useRef<any>(null);

  const convertToBase64 = (file: any) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const handlePassportChange = (e: any) => {
    setLoadingFor("passport");
    const file = e.target.files[0];
    convertToBase64(file).then((base64: any) => {
      setPayload({
        ...payload,
        documentUrls: [base64],
      });
      img1Ref.current.value = "";
      setLoadingFor("");
    });
  };

  const handleProfileChange = (e: any) => {
    setLoadingFor("profile");
    const file = e.target.files[0];
    convertToBase64(file).then((base64: any) => {
      setPayload({
        ...payload,
        profileImg: base64,
      });
      img4Ref.current.value = "";
      setLoadingFor("");
    });
  };

  const handleResubmission = () => {
    dispatch(
      updateUser(uid, {
        documentType: payload.documentType,
        documentUrls: payload.documentUrls,
        profileImg: payload.profileImg,
        status: "Re-Submitted",
        kycHistory: [
          ...user?.kycHistory,
          {
            status: "Re-Submitted",
            description: "User has re-submitted the documents",
            createdAt: new Date()?.toString(),
            action: "Re-Submission",
            name: user?.personalInfo?.name,
          },
        ],
      })
    );
    sendEmail?.mutateAsync({
      from: "noreply@yess.com",
      to: [user?.personalInfo?.email],
      subject: `KYC Update!`,
      html: `
          <div>
            <h2>Dear ${user?.personalInfo?.name},</h2>
            <p>Your KYC documents have been re-submitted successfully. Our team will review them and get back to you shortly.</p>
            <p>Thank you for your patience.</p>
            <p>Best Regards,</p>
            <p>Team Yess</p>
          </div>
        `,
      body: "",
    });
  };

  const handleUpdateUser = async () => {
    dispatch(updateUser(uid, { isPaymentCompleted: true }));
    sendEmail?.mutateAsync({
      from: "noreply@yess.com",
      to: [user?.personalInfo?.email],
      subject: `Payment Confirmation!`,
      html: `
          <div>
            <h2>Dear ${user?.personalInfo?.name},</h2>
            <p>Your KYC documents have been approved and payment has been successfully completed. You can now access your dashboard.</p>
            <p>Best Regards,</p>
            <p>Team Yess</p>
          </div>
        `,
      body: "",
    });
  };

  useEffect(() => {
    if (step === 2) {
      setTimeout(() => {
        setStep(3);
      }, 2000);
    }
  }, [step]);

  if (user && user?.status === "APPROVED" && user?.isPaymentCompleted) {
    navigate("/dashboard");
  }

  useEffect(() => {
    if (paymentIntent) {
      handleUpdateUser();
    }
    // eslint-disable-next-line
  }, [paymentIntent]);

  useEffect(() => {
    if (user) {
      setPayload({
        ...payload,
        documentType: user?.documentType,
        documentUrls: user?.documentUrls,
        profileImg: user?.profileImg,
      });
    }
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    if (!user) {
      dispatch(getLoginData(uid));
    }
  }, [dispatch, uid, user]);

  useEffect(() => {
    dispatch(getAllPromos());
    // eslint-disable-next-line
  }, []);

  return (
    <Box bg="#F5F5F5" minH="100vh" pb="24px">
      <Box
        m="24px"
        maxW="1296px"
        mx="auto"
        p="40px 130px 40px 40px"
        bg="#fff"
        borderRadius="24px"
      >
        <Flex justify="space-between" align="center" gap="24px">
          <Flex flexDir="column" gap="12px" maxW="710px" w="100%">
            <Flex flexDir="column" gap="8px">
              <Flex gap="10px" align="center">
                <CustomText
                  text="Verification"
                  size="32px"
                  weight="700"
                  color="yess.base_black"
                />
                <Flex
                  align="center"
                  justify="center"
                  h="26px"
                  w="98px"
                  borderRadius="99px"
                  border="1px solid"
                  borderColor={
                    step === 1
                      ? "#EDA145"
                      : step === 2
                      ? "#387DF5"
                      : step === 3
                      ? "#7A7474"
                      : "#2EC19E"
                  }
                  bg={
                    step === 1
                      ? "#FFF9F1"
                      : step === 2
                      ? "#EAF2FF"
                      : step === 3
                      ? "#F2F2F2"
                      : "#2EC19E"
                  }
                  mt="4px"
                >
                  <CustomText
                    text={user?.status}
                    size="12px"
                    color={
                      step === 1
                        ? "#EDA145"
                        : step === 2
                        ? "#387DF5"
                        : step === 3
                        ? "#7A7474"
                        : "#fff"
                    }
                  />
                </Flex>
              </Flex>
              <CustomText
                text="Please verify your provided information to continue."
                color="yess.neutral600"
              />
            </Flex>
            <Flex
              h="44px"
              bg="#ED3D4E"
              borderRadius="6px"
              align="center"
              justify="space-between"
              px="20px"
              display={user?.status === "REJECTED" ? "flex" : "none"}
            >
              <CustomText
                text={
                  <>
                    <strong>Verification not completed. </strong>
                    <span>{user?.remarks}</span>
                  </>
                }
                color="#fff"
                size="14px"
              />
            </Flex>
            <Flex flexDir="column" gap="24px" mt={step !== 3 ? "28px" : "0px"}>
              <Flex flexDir="column" gap="12px">
                <CustomText
                  text="Personal Info"
                  size="14px"
                  color="yess.neutral700"
                />
                <Flex
                  p="16px"
                  borderRadius="8px"
                  border="1px solid"
                  borderColor="yess.secondary_light_gray"
                  flexDir="column"
                  gap="16px"
                >
                  <Flex gap="24px" align="center">
                    <Flex flexDir="column" gap="8px" w="100%">
                      <CustomText
                        text="Name"
                        size="12px"
                        color="yess.neutral500"
                      />
                      {!user ? (
                        <SkeletonText w="120px" noOfLines={1} />
                      ) : (
                        <CustomText
                          text={user?.personalInfo?.name}
                          color="yess.neutral800"
                        />
                      )}
                    </Flex>
                    <Flex flexDir="column" gap="8px" w="100%">
                      <CustomText
                        text="Nationality"
                        size="12px"
                        color="yess.neutral500"
                      />
                      {!user ? (
                        <SkeletonText w="120px" noOfLines={1} />
                      ) : (
                        <CustomText
                          text={user?.personalInfo?.nationality}
                          color="yess.neutral800"
                        />
                      )}
                    </Flex>
                  </Flex>
                  <Divider bg="#F2F2F2" />
                  <Flex gap="24px" align="center">
                    <Flex flexDir="column" gap="8px" w="100%">
                      <CustomText
                        text="DOB"
                        size="12px"
                        color="yess.neutral500"
                      />
                      {!user ? (
                        <SkeletonText w="120px" noOfLines={1} />
                      ) : (
                        <CustomText
                          text={user?.personalInfo?.dateOfBirth || ""}
                          color="yess.neutral800"
                        />
                      )}
                    </Flex>
                    <Flex flexDir="column" gap="8px" w="100%">
                      <CustomText
                        text="Phone No"
                        size="12px"
                        color="yess.neutral500"
                      />
                      {!user ? (
                        <SkeletonText w="120px" noOfLines={1} />
                      ) : (
                        <CustomText
                          text={user?.personalInfo?.personalPhone}
                          color="yess.neutral800"
                        />
                      )}
                    </Flex>
                  </Flex>
                  <Divider bg="#F2F2F2" />
                  <Flex gap="24px" align="center">
                    <Flex flexDir="column" gap="8px" w="100%">
                      <CustomText
                        text="Email"
                        size="12px"
                        color="yess.neutral500"
                      />
                      {!user ? (
                        <SkeletonText w="120px" noOfLines={1} />
                      ) : (
                        <CustomText
                          text={
                            user?.personalInfo?.email ||
                            user?.personalInfo?.personalEmail
                          }
                          color="yess.neutral800"
                        />
                      )}
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
              <Flex flexDir="column" gap="12px">
                <CustomText
                  text="Address"
                  size="14px"
                  color="yess.neutral700"
                />
                <Flex
                  p="16px"
                  borderRadius="8px"
                  border="1px solid"
                  borderColor="yess.secondary_light_gray"
                  flexDir="column"
                  gap="16px"
                >
                  {!user ? (
                    <SkeletonText w="100%" noOfLines={1} />
                  ) : (
                    <CustomText
                      text={user?.personalInfo?.personalAddress}
                      color="yess.neutral700"
                    />
                  )}
                </Flex>
              </Flex>
              <Flex flexDir="column" gap="12px">
                <CustomText
                  text="Documents"
                  size="14px"
                  color="yess.neutral700"
                />
                <Flex
                  p="16px"
                  borderRadius="8px"
                  border="1px solid"
                  borderColor="yess.secondary_light_gray"
                  flexDir="column"
                  gap="16px"
                >
                  <Flex gap="24px" align="center">
                    <Flex flexDir="column" gap="8px" w="100%">
                      <Flex gap="5px" align="center">
                        <CustomText
                          text="IQAMA/Resident ID"
                          size="12px"
                          color={step === 3 ? "#ED3D4E" : "yess.neutral500"}
                        />
                        {step === 3 && (
                          <IconAlertTriangleFilled
                            color="#ED3D4E"
                            size="16px"
                          />
                        )}
                      </Flex>
                      {!user ? (
                        <SkeletonText w="120px" noOfLines={1} />
                      ) : (
                        <CustomText
                          text="023GHD7399HDJ"
                          color="yess.neutral800"
                        />
                      )}
                    </Flex>
                    <Flex flexDir="column" gap="8px" w="100%">
                      <CustomText
                        text="Passport Number"
                        size="12px"
                        color="yess.neutral500"
                      />
                      {!user ? (
                        <SkeletonText w="120px" noOfLines={1} />
                      ) : (
                        <CustomText text="8409384" color="yess.neutral800" />
                      )}
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
            <Box
              mt="36px"
              display={
                !user?.isPaymentCompleted && user?.status === "APPROVED"
                  ? "block"
                  : "none"
              }
            >
              {!paymentIntentInfo && (
                <CustomButton
                  btnTitle={"Make Payment"}
                  bg="yess.secondary_gold"
                  maxW="229px"
                  isLoading={paymentInfo?.isPending}
                  onClick={() => {
                    paymentInfo
                      ?.mutateAsync({
                        amount: 1000,
                        email: user?.email,
                      })
                      .then((result) => {
                        setPaymentInfo(result);

                        return;
                      })
                      .catch((err) => {
                        toast({
                          description: err?.message,
                          status: "error",
                          duration: 3000,
                          isClosable: true,
                        });
                      });
                  }}
                />
              )}
              {paymentIntentInfo && (
                <>
                  <Flex flexDir="column" gap="11px" w="100%" mb="16px">
                    <CustomText text="Promo Code" size="12px" />
                    <FormInput
                      ph="Enter Promo Code"
                      maxW="322px"
                      value={promo}
                      onChange={(e: any) => setPromo(e.target.value)}
                    />
                    <CustomButton
                      btnTitle={
                        promos?.find(
                          (tempPromo: any) =>
                            tempPromo.code?.toLowerCase() ===
                            promo?.toLowerCase()
                        )
                          ? "Applied"
                          : "Apply"
                      }
                      bg="yess.secondary_gold"
                      maxW="120px"
                      onClick={() => {
                        const promoCode = promos?.find(
                          (tempPromo: any) => tempPromo.code === promo
                        );
                        if (promoCode) {
                          setPaymentInfo({
                            ...paymentIntentInfo,
                            amount: paymentIntentInfo.amount - promoCode.amount,
                          });
                        } else {
                          toast({
                            title: "Invalid Promo Code",
                            status: "error",
                            duration: 3000,
                            isClosable: true,
                          });
                        }
                      }}
                    />
                  </Flex>
                  {!promos?.find(
                    (tempPromo: any) =>
                      tempPromo.code?.toLowerCase() === promo?.toLowerCase()
                  ) ? (
                    <PaymentForm
                      paymentIntent={paymentIntentInfo?.client_secret}
                    />
                  ) : (
                    <Flex flexDir="column" gap="16px">
                      <CustomText
                        text="You have successfully applied the promo code. Please complete the payment to proceed."
                        color="yess.base_black"
                      />
                      <CustomButton
                        btnTitle="Complete Payment"
                        bg="yess.secondary_gold"
                        maxW="229px"
                        onClick={() => {
                          handleUpdateUser();
                        }}
                      />
                    </Flex>
                  )}
                </>
              )}
            </Box>
            <Flex
              mt="36px"
              display={user?.status === "REJECTED" ? "flex" : "none"}
              gap="16px"
              flexDir="column"
            >
              <CustomText text="Re-upload documents" color="yess.neutral700" />
              <Box
                pos="relative"
                display={!user?.kycInfo?.documents ? "block" : "none"}
              >
                <Flex
                  flexDir="column"
                  gap="11px"
                  w="100%"
                  cursor="pointer"
                  onClick={() => {
                    setIsMenuOpen(!isMenuOpen);
                  }}
                >
                  <CustomText text="Document Type" size="12px" />
                  <Flex
                    px="24px"
                    h="38px"
                    align="center"
                    justify="space-between"
                    bg="#f2f2f2"
                    borderRadius="8px"
                  >
                    <CustomText
                      text={payload?.documentType}
                      size="14px"
                      color="yess.neutral_black"
                    />
                    <IconChevDown />
                  </Flex>
                </Flex>
                <Flex
                  flexDir="column"
                  gap="12px"
                  p="12px 24px"
                  bg="#f2f2f2"
                  borderRadius="12px"
                  pos="absolute"
                  mt="5px"
                  w="100%"
                  zIndex="1000"
                  display={isMenuOpen ? "flex" : "none"}
                  boxShadow={`0px 4px 4px rgba(0, 0, 0, 0.25)`}
                >
                  {["Passport", "Driving License", "ID Card"]?.map(
                    (type, idx) => (
                      <Flex
                        key={idx}
                        align="center"
                        cursor="pointer"
                        justify="space-between"
                        onClick={() => {
                          setIsMenuOpen(false);
                          setPayload({
                            ...payload,
                            documentType: type,
                            documentUrls: [],
                          });
                        }}
                      >
                        <CustomText
                          text={type}
                          size="12px"
                          color="yess.nuetral900"
                          cursor="pointer"
                        />
                        {payload?.documentType === type && (
                          <IconCheck size="12px" color="#000000" />
                        )}
                      </Flex>
                    )
                  )}
                </Flex>
              </Box>

              <Flex
                flexDir="column"
                gap="11px"
                w="100%"
                display={!user?.kycInfo?.documents ? "flex" : "none"}
              >
                <CustomText
                  text={`Upload ${payload?.documentType} Picture`}
                  size="12px"
                />
                <Flex
                  p="32px"
                  w="100%"
                  align="center"
                  justify="center"
                  flexDir="column"
                  gap="16px"
                  bg="#F2F2F2"
                  borderRadius="16px"
                  border="1px dashed #908B8B"
                >
                  {payload?.documentUrls?.length > 0 ? (
                    <>
                      <Image
                        src={payload?.documentUrls[0]}
                        alt="passport"
                        objectFit="cover"
                      />
                      <CustomButton
                        btnTitle="Browse"
                        maxW="111px"
                        height="36px"
                        radius="8px"
                        bg="yess.secondary_gold"
                        size="14px"
                        weight="400"
                        isLoading={loadingFor === "passport"}
                        onClick={() => img1Ref.current.click()}
                      />
                    </>
                  ) : (
                    <>
                      <UploadIcon />
                      <CustomText text="Browse Files" color="yess.base_black" />
                      <CustomButton
                        btnTitle="Browse"
                        maxW="111px"
                        height="36px"
                        radius="8px"
                        bg="yess.secondary_gold"
                        size="14px"
                        weight="400"
                        isLoading={loadingFor === "passport"}
                        onClick={() => img1Ref.current.click()}
                      />
                    </>
                  )}
                </Flex>
              </Flex>

              <Flex
                flexDir="column"
                gap="11px"
                w="100%"
                display={!user?.kycInfo?.profileImg ? "flex" : "none"}
              >
                <CustomText text="Upload Profile Picture" size="12px" />
                <Flex
                  p="32px"
                  w="100%"
                  align="center"
                  justify="center"
                  flexDir="column"
                  gap="16px"
                  bg="#F2F2F2"
                  borderRadius="16px"
                  border="1px dashed #908B8B"
                >
                  {payload?.profileImg ? (
                    <>
                      <Image
                        src={payload?.profileImg}
                        alt="passport"
                        objectFit="cover"
                      />
                      <CustomButton
                        btnTitle="Browse"
                        maxW="111px"
                        height="36px"
                        radius="8px"
                        bg="yess.secondary_gold"
                        size="14px"
                        weight="400"
                        isLoading={loadingFor === "profile"}
                        onClick={() => img4Ref.current.click()}
                      />
                    </>
                  ) : (
                    <>
                      <UploadIcon />
                      <CustomText text="Browse Files" color="yess.base_black" />
                      <CustomButton
                        btnTitle="Browse"
                        maxW="111px"
                        height="36px"
                        radius="8px"
                        bg="yess.secondary_gold"
                        size="14px"
                        weight="400"
                        isLoading={loadingFor === "profile"}
                        onClick={() => img4Ref.current.click()}
                      />
                    </>
                  )}
                </Flex>
              </Flex>

              <CustomButton
                btnTitle="Re-Submit"
                bg="yess.secondary_gold"
                maxW="229px"
                onClick={() => {
                  handleResubmission();
                }}
              />

              <input
                type="file"
                hidden
                ref={img1Ref}
                onChange={handlePassportChange}
              />

              <input
                type="file"
                hidden
                ref={img4Ref}
                onChange={handleProfileChange}
              />
            </Flex>
          </Flex>
          <KYCVector />
        </Flex>
      </Box>
    </Box>
  );
}

export default KYCPending;
